import { band } from '../lib/results';
import type { ProgressPoint } from '../lib/progress';

/**
 * A candidate's overall across their tests, as one line.
 *
 * Drawn left to right the first time it is seen (the page decides), then
 * simply there. The scale is the candidate's own range with half a band
 * either side, so a move from 6 to 6.5 reads as the step it is.
 */
export function ProgressLine({ points, fresh }: { points: ProgressPoint[]; fresh: boolean }) {
  if (points.length < 2) return null;
  const W = 600;
  const H = 120;
  const pad = { x: 18, y: 18 };
  const values = points.map((p) => p.overall);
  const lo = Math.min(...values) - 0.5;
  const hi = Math.max(...values) + 0.5;
  const x = (i: number) => pad.x + (i / (points.length - 1)) * (W - pad.x * 2);
  const y = (v: number) => pad.y + (1 - (v - lo) / (hi - lo)) * (H - pad.y * 2);
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.overall).toFixed(1)}`).join(' ');
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const change = last.overall - first.overall;

  return (
    <section className={`progress${fresh ? ' progress--fresh' : ''}`}>
      <header className="progress__head">
        <p className="progress__title">Your overall across {points.length} tests</p>
        <p className="progress__change">
          {band(first.overall)} → <b>{band(last.overall)}</b>
          {change !== 0 && (
            <span className={change > 0 ? 'progress__up' : 'progress__down'}>
              {change > 0 ? '+' : '−'}
              {Math.abs(change).toFixed(1)}
            </span>
          )}
        </p>
      </header>
      {/* The line stretches to the width it is given; the dots are laid over
          it in percentages so they stay round however wide it is. */}
      <div
        className="progress__chart"
        role="img"
        aria-label={`Overall band across your tests: ${points.map((p) => band(p.overall)).join(', ')}.`}
      >
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
          <path className="progress__line" d={path} />
        </svg>
        {points.map((p, i) => (
          <span
            key={p.name}
            className={`progress__dot${i === points.length - 1 ? ' progress__dot--last' : ''}`}
            title={`${p.name}: ${band(p.overall)}`}
            style={{
              left: `${(x(i) / W) * 100}%`,
              top: `${(y(p.overall) / H) * 100}%`,
              animationDelay: `${200 + (i / (points.length - 1)) * 700}ms`,
            }}
          />
        ))}
      </div>
    </section>
  );
}
